import { MutationOptions, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '~/hooks';
import {
  createPaymentApi,
  cancelPaymentApi,
  verifyPaymentApi,
  CreatePaymentData,
  PaymentResponse,
} from '../repository';
import {
  BALANCE_QUERY_KEYS,
  WALLET_QUERY_KEYS,
  PAYMENT_QUERY_KEYS,
} from '../queries';

/**
 * Mutation hook for creating a payment
 */
export const useCreatePaymentMutation = (
  options?: MutationOptions<
    PaymentResponse,
    CustomAxiosError,
    CreatePaymentData
  >,
) => {
  return useApiMutation({
    ...options,
    mutationFn: (data: CreatePaymentData) => createPaymentApi(data),
  });
};

/**
 * Mutation hook for cancelling a payment
 */
export const useCancelPaymentMutation = (
  options?: MutationOptions<PaymentResponse, CustomAxiosError, string>,
) => {
  return useApiMutation({
    ...options,
    mutationFn: (paymentId: string) => cancelPaymentApi(paymentId),
  });
};

/**
 * Mutation hook for verifying a payment
 * Automatically invalidates balance and wallet queries when payment is confirmed
 */
export const useVerifyPaymentMutation = (
  options?: MutationOptions<
    {
      success: boolean;
      message: string;
      data: PaymentResponse & { confirmations?: number; required?: number };
    },
    CustomAxiosError,
    string
  >,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: (paymentId: string) => verifyPaymentApi(paymentId),
    onSuccess: (response, ...rest) => {
      // If payment is confirmed, invalidate balance and wallet queries
      if (response.data.status === 'CONFIRMED') {
        queryClient.invalidateQueries({
          queryKey: [BALANCE_QUERY_KEYS.BALANCES],
        });
        queryClient.invalidateQueries({
          queryKey: [WALLET_QUERY_KEYS.WALLETS],
        });
        queryClient.invalidateQueries({
          queryKey: [PAYMENT_QUERY_KEYS.ACTIVE_PAYMENT],
        });
      }
      options?.onSuccess?.(response, ...rest);
    },
  });
};
